import type { ResourceListOptions } from '#dn-types/api.ts';
import type { ConfigOverride } from '#dn-types/host.ts';
import type { Network } from '#dn-types/network.ts';
import { rewordValidationMessages } from './errors.ts';
import { del, get, getList, post, put } from './fetch.ts';

export const DEFAULT_NETWORKS_PAGE_SIZE = 25;

// GET /v2/networks
export type GetNetworksResult = Network[];
type GetNetworksVariables = ResourceListOptions;

export function getNetworks({ cursor, pageSize = DEFAULT_NETWORKS_PAGE_SIZE, init }: GetNetworksVariables = {}) {
  const params = new URLSearchParams({
    includeCounts: 'true',
    pageSize: pageSize.toString(),
  });

  if (cursor) {
    params.append('cursor', cursor);
  }

  return getList<GetNetworksResult>(`/v2/networks?${params.toString()}`, init).then((res) => {
    const page = res.metadata.page;
    // If the user paginates backwards, we may send a cursor which results in loading the "first" page of results
    // but with fewer results than the standard page size. Refetch the first page without a cursor to get a full result set.
    if (page?.start === 0 && page.count < pageSize && cursor) {
      params.delete('cursor');
      return getList<GetNetworksResult>(`/v2/networks?${params.toString()}`, init);
    }
    return res;
  });
}

// GET /v2/networks/:id
export type GetNetworkResult = Network;
export function getNetwork(id: string, init?: RequestInit) {
  return get<GetNetworkResult>(`/v2/networks/${encodeURIComponent(id)}`, init);
}

// PUT /v2/networks/:id
export type EditNetworkVariables = {
  id: string;
  values: {
    name: string;
    description: string;
    lighthousesAsRelays: boolean;
    disableManagedLighthouses?: boolean;
  };
};
export type EditNetworkResult = Network;

export function editNetwork({ id, values }: EditNetworkVariables) {
  return (
    put<EditNetworkResult>(`/v2/networks/${encodeURIComponent(id)}`, values)
      // Convert from server error codes to more friendly strings, if possible
      .catch((error: unknown) => {
        rewordValidationMessages(error, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have a network with this name, try another';
          }
        });
      })
  );
}

// POST /v2/networks
export type AddNetworkVariables = {
  name: string;
  description: string;
  cidrs: string[];
  lighthousesAsRelays?: boolean;
  curve?: '25519' | 'P256';
  disableManagedLighthouses?: boolean;
};
export type AddNetworkResult = Network;
export function addNetwork(formData: AddNetworkVariables) {
  return (
    post<AddNetworkResult>('/v2/networks', formData)
      // Convert from server error codes to more friendly strings, if possible
      .catch((error: unknown) => {
        rewordValidationMessages(error, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have a network with this name, try another';
          }
        });
      })
  );
}

// POST /v2/networks/:id/cidrs
export type AddNetworkCIDRVariables = {
  id: string;
  cidr: string;
};
export type AddNetworkCIDRResult = Network;

export function addNetworkCIDR({ id, cidr }: AddNetworkCIDRVariables) {
  return (
    post<AddNetworkCIDRResult>(`/v2/networks/${encodeURIComponent(id)}/cidrs`, { cidr })
      // Convert from server error codes to more friendly strings, if possible
      .catch((error: unknown) => {
        rewordValidationMessages(error, (details) => {
          if (details.code === 'ERR_NETWORK_ALREADY_HAS_IPV4_CIDR') {
            return 'This network already has an IPv4 CIDR. Only one IPv4 CIDR is supported per network.';
          }
        });
      })
  );
}

// GET /v1/networks/:id/config-overrides
export type GetNetworkConfigOverridesResult = { overrides: ConfigOverride[] };
export function getNetworkConfigOverrides(id: string, init?: RequestInit) {
  return get<GetNetworkConfigOverridesResult>(`/v1/networks/${encodeURIComponent(id)}/config-overrides`, init);
}

// PUT /v1/networks/:id/config-overrides
// Full replace: the network's overrides become exactly the submitted set.
export type SetNetworkConfigOverridesVariables = {
  id: string;
  overrides: ConfigOverride[];
};
export type SetNetworkConfigOverridesResult = { overrides: ConfigOverride[] };
export function setNetworkConfigOverrides({ id, overrides }: SetNetworkConfigOverridesVariables) {
  return put<SetNetworkConfigOverridesResult>(`/v1/networks/${encodeURIComponent(id)}/config-overrides`, { overrides });
}

// DELETE /v1/networks/:id
export type DeleteNetworkResult = Record<string, never>;
export function deleteNetwork(id: string, init?: RequestInit) {
  return (
    del<DeleteNetworkResult>(`/v1/networks/${encodeURIComponent(id)}`, init)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === undefined && details.code === 'ERR_HAS_DEPENDENTS') {
            return 'This network contains hosts, relays, or lighthouses, or is used by an endpoint SSO provider. Remove them and try again.';
          }
        });
      })
  );
}

// POST /v1/networks/:id/update
export type UpdateNetworkResult = Network;
export function updateNetwork(id: string) {
  return (
    post<UpdateNetworkResult>(`/v1/networks/${encodeURIComponent(id)}/update`, {})
      // Convert from server error codes to more friendly strings, if possible
      .catch((error: unknown) => {
        rewordValidationMessages(error, (details) => {
          if (details.code === 'ERR_NO_UPDATES_AVAILABLE') {
            return 'This network has already been updated.';
          }
        });
      })
  );
}
